/**
 * 🌙 BIG FACE LUNA - Luna avec GRAND visage et choix interactifs
 * 🔥 BIG FACE ENZO - Enzo avec GRAND visage et choix interactifs
 * 
 * Design "Portrait Cinématique" - Les guides prennent de la place !
 * - Grand portrait (~50% de l'écran)
 * - Choix interactifs ludiques
 * - Animation fluide entre les réponses
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Heart, Star, ChevronRight, X, 
  MessageCircle, Coffee, Users, Moon, Sun, Flame, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { GUIDE_IMAGES, GUIDE_CONFIG } from './soul/GuideAvatar';

// 🎯 Messages de Luna avec choix interactifs
const LUNA_CONVERSATIONS = {
  welcome: {
    message: "Coucou toi ! 💜 Je suis Luna, ta complice sur Heartbeats. L'amour est au coin de la rue... Qu'est-ce qui t'amène ici ?",
    expression: 'welcome_smile',
    choices: [
      { id: 'meet', label: '💕 Trouver l\'amour', icon: Heart, next: 'looking_love' },
      { id: 'chat', label: '💬 Juste papoter', icon: MessageCircle, next: 'just_chat' },
      { id: 'curious', label: '🤔 Je découvre', icon: Star, next: 'discovering' },
    ]
  },
  looking_love: {
    message: "Oh ! Tu cherches l'amour ! 😍 Je sens que tu es quelqu'un de bien... Dis-moi, tu préfères quelqu'un de...",
    expression: 'romantic_gaze',
    choices: [
      { id: 'funny', label: '😂 Drôle et léger', icon: Sun, next: 'funny_match' },
      { id: 'deep', label: '🌙 Profond et mystérieux', icon: Moon, next: 'deep_match' },
      { id: 'both', label: '✨ Un peu des deux !', icon: Sparkles, next: 'perfect_match' },
    ]
  },
  just_chat: {
    message: "J'adore ! 🤗 Parfois on a juste besoin de parler... Les salons sont faits pour ça ! Tu veux que je te montre ?",
    expression: 'warm_laugh',
    choices: [
      { id: 'yes', label: '👍 Oui, montre-moi !', icon: ChevronRight, action: 'open_salons' },
      { id: 'later', label: '⏳ Plus tard', icon: null, next: 'see_you_later' },
    ]
  },
  discovering: {
    message: "Bienvenue dans l'aventure ! 🌟 Heartbeats, c'est un espace de positivité où tu peux rencontrer des gens près de chez toi... Pas de swipe ici, on respecte les gens !",
    expression: 'excited_look',
    choices: [
      { id: 'tell_more', label: '🤩 Raconte-moi plus !', icon: Star, next: 'explain_more' },
      { id: 'try_now', label: '🚀 Je veux essayer !', icon: ChevronRight, action: 'start_profile' },
    ]
  },
  funny_match: {
    message: "Tu aimes rire ! Moi aussi 😊 Je vais te trouver quelqu'un qui te fera sourire... Laisse-moi deviner tes passions !",
    expression: 'wink_playful',
    choices: [
      { id: 'start', label: '💜 C\'est parti !', icon: Heart, action: 'start_quiz' },
    ]
  },
  deep_match: {
    message: "Les âmes profondes s'attirent... 🌙 Je vais te connecter avec quelqu'un qui comprend vraiment. Tu es prêt(e) ?",
    expression: 'dreamy_gaze',
    choices: [
      { id: 'start', label: '💜 Je suis prêt(e)', icon: Heart, action: 'start_quiz' },
    ]
  },
  perfect_match: {
    message: "Quelqu'un de complet ! Tu sais ce que tu veux ! 😏 Je vais te trouver une perle rare... Fais-moi confiance !",
    expression: 'seductive_gaze',
    choices: [
      { id: 'start', label: '💜 Je te fais confiance', icon: Heart, action: 'start_quiz' },
    ]
  },
  explain_more: {
    message: "Imagine un endroit où tu retrouves des gens de ton coin, des personnes que tu croises au magasin... On papote, on refait le monde, on rompt la solitude. C'est Heartbeats ! 💜",
    expression: 'tender_support',
    choices: [
      { id: 'join', label: '🎉 Je veux en faire partie !', icon: Users, action: 'start_profile' },
      { id: 'salons', label: '💬 Voir les salons', icon: MessageCircle, action: 'open_salons' },
    ]
  },
  see_you_later: {
    message: "Pas de souci ! 😊 Je serai là quand tu voudras. N'hésite pas à revenir me voir... À très vite ! 💜",
    expression: 'satisfied_smile',
    choices: [
      { id: 'bye', label: '👋 À bientôt Luna !', icon: null, action: 'close' },
    ]
  }
};

// 🔥 Messages de Enzo avec choix interactifs
const ENZO_CONVERSATIONS = {
  welcome: {
    message: "Yo ! 🔥 Moi c'est Enzo. Bienvenue dans l'arène ! Alors, t'es là pour quoi exactement ?",
    expression: 'welcome_smile',
    choices: [
      { id: 'meet', label: '🔥 Draguer !', icon: Flame, next: 'looking_action' },
      { id: 'chat', label: '💬 Chiller et discuter', icon: MessageCircle, next: 'chill_mode' },
      { id: 'curious', label: '🤷 Je matte juste', icon: Star, next: 'just_looking' },
    ]
  },
  looking_action: {
    message: "J'aime ton énergie ! 💪 Tu sais ce que tu veux. Alors dis-moi, tu préfères les challenges ou les victoires faciles ?",
    expression: 'confident_smirk',
    choices: [
      { id: 'challenge', label: '🏆 Les challenges !', icon: Zap, next: 'challenge_mode' },
      { id: 'easy', label: '😎 Cool et tranquille', icon: Sun, next: 'easy_mode' },
    ]
  },
  chill_mode: {
    message: "Relax, j'aime ça ! 🤙 Les salons sont parfaits pour ça. On discute, on rigole, zéro pression. Tu veux voir ?",
    expression: 'warm_laugh',
    choices: [
      { id: 'yes', label: '👍 Let\'s go !', icon: ChevronRight, action: 'open_salons' },
      { id: 'later', label: '⏳ Après', icon: null, next: 'see_you_later' },
    ]
  },
  just_looking: {
    message: "T'inquiète, je juge pas ! 😏 Mais entre nous, y'a des pépites ici. Tu veux que je te montre comment ça marche ?",
    expression: 'knowing_smirk',
    choices: [
      { id: 'show_me', label: '🧐 Montre-moi', icon: Star, next: 'explain_more' },
      { id: 'no_thanks', label: '✌️ Je gère', icon: null, action: 'close' },
    ]
  },
  challenge_mode: {
    message: "Un warrior ! 🔥 J'adore. Ici on match par compatibilité, pas par hasard. Les vraies connexions, ça se mérite !",
    expression: 'excited_look',
    choices: [
      { id: 'start', label: '💪 Je suis chaud !', icon: Flame, action: 'start_quiz' },
    ]
  },
  easy_mode: {
    message: "Smart move ! 😎 Pas besoin de se prendre la tête. Je vais te trouver des profils qui te correspondent. Confiance !",
    expression: 'wink_playful',
    choices: [
      { id: 'start', label: '🤝 Deal !', icon: Heart, action: 'start_quiz' },
    ]
  },
  explain_more: {
    message: "C'est simple : ici, pas de swipe débile. On matche sur la vraie compatibilité - passions, valeurs, vibes. Du solide ! 💪",
    expression: 'confident_smirk',
    choices: [
      { id: 'join', label: '🔥 Je teste !', icon: Flame, action: 'start_profile' },
      { id: 'salons', label: '💬 D\'abord les salons', icon: MessageCircle, action: 'open_salons' },
    ]
  },
  see_you_later: {
    message: "No stress bro ! 🤙 Je serai là. Reviens quand tu veux, les portes sont toujours ouvertes !",
    expression: 'satisfied_smile',
    choices: [
      { id: 'bye', label: '✌️ À plus Enzo !', icon: null, action: 'close' },
    ]
  }
};

// Configuration des guides
const GUIDE_DATA = {
  luna: {
    name: 'Luna',
    conversations: LUNA_CONVERSATIONS,
    gradient: 'from-pink-500 via-purple-500 to-indigo-600',
    accentColor: 'rgba(236, 72, 153, 0.5)',
    subtitle: 'Ta complice Heartbeats 💜',
    getImage: (expr) => GUIDE_IMAGES.luna?.[expr] || GUIDE_IMAGES.luna?.default,
  },
  enzo: {
    name: 'Enzo',
    conversations: ENZO_CONVERSATIONS,
    gradient: 'from-red-500 via-orange-500 to-amber-500',
    accentColor: 'rgba(239, 68, 68, 0.5)',
    subtitle: 'Ton coach Heartbeats 🔥',
    getImage: (expr) => GUIDE_IMAGES.enzo?.[expr] || GUIDE_IMAGES.enzo?.default,
  }
};

/**
 * 🎭 BigFaceGuideChat - Guide avec grand visage et choix interactifs
 * Utilisable pour Luna OU Enzo
 */
export function BigFaceGuideChat({ 
  guide = 'luna',
  customerName = 'toi',
  isDarkMode = false,
  onAction,
  onClose,
  startConversation = 'welcome'
}) {
  const [currentStep, setCurrentStep] = useState(startConversation);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const guideData = GUIDE_DATA[guide] || GUIDE_DATA.luna;
  const conversation = guideData.conversations[currentStep] || guideData.conversations.welcome;
  const currentImage = guideData.getImage(conversation.expression);
  
  // Personnaliser le message avec le prénom
  const personalizedMessage = conversation.message.replace('[Prénom]', customerName);
  
  const handleChoice = (choice) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    setTimeout(() => {
      if (choice.action) {
        // Action spéciale
        onAction?.(choice.action);
        if (choice.action === 'close') {
          onClose?.();
        }
      } else if (choice.next) {
        // Passer à l'étape suivante
        setCurrentStep(choice.next);
      }
      setIsAnimating(false);
    }, 300);
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9, y: 50 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 50 }}
      className={`fixed bottom-4 right-4 z-50 w-[340px] md:w-[380px] rounded-2xl overflow-hidden shadow-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}
      style={{ 
        boxShadow: `0 25px 80px ${guideData.accentColor}, 0 0 60px rgba(168, 85, 247, 0.2)`,
        maxHeight: '85vh'
      }}
      data-testid={`big-face-${guide}`}
    >
      {/* === SECTION PORTRAIT (50% de la carte) === */}
      <div 
        className="relative overflow-hidden"
        style={{ height: '200px' }}
      >
        {/* Fond dégradé */}
        <div className={`absolute inset-0 bg-gradient-to-br ${guideData.gradient}`} />
        
        {/* Particules flottantes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-white/30"
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + (i % 3) * 25}%`,
              }}
              animate={{
                y: [0, -15, 0],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 2 + i * 0.2,
                repeat: Infinity,
                delay: i * 0.15,
              }}
            />
          ))}
        </div>
        
        {/* Bouton fermer */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        )}
        
        {/* 🎭 GRAND VISAGE */}
        <div className="absolute inset-0 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={conversation.expression}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="relative"
            >
              {/* Glow effect */}
              <div 
                className="absolute inset-0 rounded-full blur-2xl opacity-50"
                style={{ background: guideData.accentColor, transform: 'scale(1.3)' }}
              />
              
              {/* Image du guide */}
              <motion.img
                src={currentImage}
                alt={guideData.name}
                className="relative w-32 h-32 md:w-36 md:h-36 rounded-full object-cover"
                style={{
                  boxShadow: `0 0 40px ${guideData.accentColor}, 0 15px 30px rgba(0,0,0,0.4)`
                }}
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
          </AnimatePresence>
        </div>
        
        {/* Nom en bas */}
        <div className="absolute bottom-2 left-0 right-0 text-center">
          <h3 className="text-xl font-black text-white drop-shadow-lg">{guideData.name}</h3>
          <p className="text-white/80 text-xs">{guideData.subtitle}</p>
        </div>
      </div>
      
      {/* === SECTION MESSAGE + CHOIX === */}
      <div className="p-4 max-h-[300px] overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25 }}
          >
            {/* Bulle de message */}
            <div className={`p-3 rounded-xl mb-3 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {personalizedMessage}
              </p>
            </div>
            
            {/* Choix interactifs */}
            <div className="space-y-2">
              {conversation.choices.map((choice, idx) => {
                const Icon = choice.icon;
                return (
                  <motion.button
                    key={choice.id}
                    initial={{ opacity: 0, x: -15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + idx * 0.08 }}
                    onClick={() => handleChoice(choice)}
                    disabled={isAnimating}
                    className={`
                      w-full p-2.5 rounded-xl text-left transition-all
                      flex items-center gap-2 group text-sm
                      ${isDarkMode 
                        ? 'bg-gray-800/50 hover:bg-gray-700 border border-gray-700 hover:border-gray-600' 
                        : 'bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 shadow-sm'
                      }
                      ${isAnimating ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {Icon && (
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                        bg-gradient-to-br ${guideData.gradient} text-white
                        group-hover:scale-110 transition-transform
                      `}>
                        <Icon className="w-4 h-4" />
                      </div>
                    )}
                    <span className={`font-medium flex-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {choice.label}
                    </span>
                    <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} group-hover:translate-x-1 transition-transform`} />
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// Export pour compatibilité
export const BigFaceLuna = (props) => <BigFaceGuideChat guide="luna" {...props} />;
export const BigFaceEnzo = (props) => <BigFaceGuideChat guide="enzo" {...props} />;

export default BigFaceGuideChat;
